import { useQueryClient } from '@tanstack/react-query';
import { Input } from 'antd';
import { useEffect, useState } from 'react';
import { updateHourRateForMonth } from '../../fetchHooks/updateHourRateForMonth';
import type { DayType } from './Day';

type HourRateInputDebouncedProps = {
  label: string;
  registration?: DayType;
  value?: number;
  style?: React.CSSProperties;
  disabled?: boolean;
};

interface HourRateUpdateData {
  hourRate: number;
  id: number;
  month: number;
  year: number;
}

export const HourRateInputDebounced = ({
  label,
  registration,
  value,
  style,
  disabled,
}: HourRateInputDebouncedProps) => {
  const [inputValue, setInputValue] = useState<number | 'NaN' | undefined>();
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const queryClient = useQueryClient();

  const isValueUpdated = inputValue === value;

  const handleChangeHourRate = (data = {}) =>
    updateHourRateForMonth({ ...data }, () => {
      queryClient.invalidateQueries({ queryKey: ['useRegistrations'] });
      queryClient.invalidateQueries({ queryKey: ['useYearStats'] });
    });

  useEffect(() => {
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [debounceTimer]);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const textInputValue = event.target.value === 'NaN' ? '0' : event.target.value;
    try {
      let val = 0;
      if (textInputValue !== 'NaN') val = Number.parseFloat(textInputValue);
      if (Number.isNaN(val)) val = 0;
      setInputValue(val);
      // Clear previous timer if any
      if (debounceTimer) clearTimeout(debounceTimer);
      if (!registration) return;
      const newDebounceTimer = setTimeout(() => {
        handleChangeHourRate({
          hourRate: val,
          id: registration.id,
          month: registration.month,
          year: registration.year,
        } as HourRateUpdateData);
      }, 2000);

      setDebounceTimer(newDebounceTimer);
    } catch (error) {
      console.error('Invalid input: ', error);
    }
  };

  // if (!registration || !value) return null;
  return (
    <Input
      placeholder={label}
      value={inputValue?.toString() || ''}
      addonBefore={label + ':'}
      status={!isValueUpdated ? 'warning' : undefined}
      onChange={handleInputChange}
      style={style}
      disabled={disabled}
    />
  );
};
